import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { DataConverter } from "../Converters/DataConverter";
import { SecurityUtils } from "../Security/SecurityUtils";
import { StringUtils } from "../Utils/StringUtils";
import { IEncAttrValue } from "./IAttrValue";

const logger = LazyLoadingSectionLogger.create(LoggingSection.encryption);

/**
 * Šifrovaná hodnota atributu aplikačního objektu
 */
export class EncAttrValue implements IEncAttrValue {
    /**
     * Zašifrovaná hodnota
     */
    private readonly _encryptedValue: string;
    /**
     * Zda i v zašifrované podobě obsahuje zašifrovanou nebo otevřenou hodnotu. Např. u dokumentů je to nešifrovaný identifikátor dokumentu nebo u vazeb je to obdobně.
     */
    private readonly _isOpenedValue: boolean;
    private _isEncrypted: boolean = true;
    private _value: any;
    private _deserializer: (decryptedVal: string) => any;

    /**
     * Zda obsahuje právě zašifrovanou nebo rozšifrovanou hodnotu
     */
    public get isEncrypted(): boolean {
        return this._isEncrypted;
    }

    /**
     * Zda je zašifrován neprázdný atribut.
     */
    public get hasEncryptedValue(): boolean {
        return (this._encryptedValue != null && this._encryptedValue.length > 0);
    }

    constructor(encryptedValue: string, isOpenedValue: boolean, defaultValue: any, deserializer: (decryptedVal: string) => any) {
        this._value = defaultValue;
        this._encryptedValue = encryptedValue;
        this._isOpenedValue = isOpenedValue;

        this._deserializer = deserializer ? deserializer : (decryptedVal: string) => decryptedVal;
    }

    public valueOf(): any {
        return this._value;
    }

    public toString(): string {
        return this._value != null ? this._value.toString() : "";
    }

    /**
     * Dešifruje hodnotu atributu předaným klíčem a pomocí deserializeru ji převede na původní datový typ
     * @param key Dešifrovací klíč
     * @param deserializer Funkce převádějící textovou formu hodnoty na správný datový typ
     * @returns True pokud se povedlo dešifrovat nebo to nebylo třeba, jinak false
     */
    public async decrypt(key: CryptoKey): Promise<boolean> {
        if (!this.isEncrypted)
            return true;

        if (StringUtils.isNullOrEmpty(this._encryptedValue)) {
            this._isEncrypted = false;
            return true;
        }

        try {
            const decryptedValue = this._isOpenedValue ? this._encryptedValue : await SecurityUtils.decryptString(DataConverter.base64ToArray(this._encryptedValue), key);
            this._value = this._deserializer(decryptedValue);
            this._isEncrypted = false;
        } catch (e) {
            logger.error("Unable to decrypt attribute value - probably incorrect password", e);
            return false;
        }

        return true;
    }
}
